import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { isApiError } from "@bw/shared/api";

import {
  getMyOrg,
  isOrgWorkspaceResponse,
  getOnboardingState,
  type Org,
  type MyOrgNotFoundResponse,
  type OrgWorkspace,
  type OnboardingState,
} from "@/libs/onboardingApi";
import { useSelectedOrganization } from "@/app/OrganizationContext";
import { STAFF_SETUP_ALLOWED_STATUSES } from "@/app/org-access-policy";

const ORG_NOT_FOUND_CODE: MyOrgNotFoundResponse["detail"]["code"] =
  "ORG_NOT_FOUND";

/**
 * useMyOrg — the single org-status seam every onboarding/approval layer reads.
 *
 * Restores the original 16fd6b71 gate on a React-Query mechanism. `GET /orgs/me`
 * is mapped to a discriminated `OrgStatusView` so callers branch on a closed set
 * of `kind`s instead of re-deriving status semantics in three places.
 *
 * Fail-closed by construction: unknown backend statuses and malformed payloads
 * map to `error` (locked, never `live`), only the stable ORG_NOT_FOUND 404 maps
 * to `no-org`, and every other failure also stays locked. Only `active` unlocks
 * the full app; only an explicit `approved/active` clears the Layer-2 lock.
 *
 * Retry policy: retry ONLY 5xx (transient), capped at 3 retries (4 calls total).
 * All 4xx responses are definitive and never retried; only ORG_NOT_FOUND maps
 * to the no-org registration state.
 */

export type OrgStatusView = (
  | { kind: "loading"; org?: undefined }
  | { kind: "no-org"; org?: undefined }
  | {
      kind: "selection-required";
      organizations: OrgWorkspace[];
      org?: undefined;
    }
  | { kind: "error"; message: string; org?: Org }
  | {
      kind: "onboarding";
      org: Org;
      status: "pending" | "in_review" | "approved";
    }
  | { kind: "rejected"; org: Org }
  | { kind: "suspended"; org: Org }
  | { kind: "live"; org: Org }
) & { staffCapabilityPending?: boolean };

/** Routes always reachable while the org is locked — prevents a redirect loop
 * (the gate's own destination must be allowed). Prefix match. */
export const LOCKED_ALLOWED_PREFIXES = ["/getting-started"] as const;

export function isAllowedWhileLocked(pathname: string): boolean {
  return LOCKED_ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

const ONBOARDING_STATUSES = new Set(
  [...STAFF_SETUP_ALLOWED_STATUSES].filter((status) => status !== "active"),
);

/** Base for the exponential retry backoff (ms). Small so a hard 5xx failure
 * resolves to the error view promptly rather than stalling the gate. */
const RETRY_BASE_MS = 30;

function toView(org: Org): OrgStatusView {
  const status = org.status as string;
  switch (status) {
    case "active":
      return { kind: "live", org };
    case "rejected":
      return { kind: "rejected", org };
    case "suspended":
      return { kind: "suspended", org };
    case "pending":
    case "in_review":
    case "approved":
      return {
        kind: "onboarding",
        org,
        status: status as "pending" | "in_review" | "approved",
      };
    default:
      // Defense in depth if a caller bypasses getMyOrg's response validator.
      return {
        kind: "error",
        message: "The server returned an invalid organization status.",
      };
  }
}

export function useMyOrg(): OrgStatusView {
  const { organizationId, clearOrganization } = useSelectedOrganization();
  const query = useQuery({
    queryKey: ["org", "me", organizationId],
    queryFn: () => getMyOrg(organizationId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // Retry transient 5xx only, capped at 3 (4 calls total). 4xx failures are
    // definitive — never retried.
    retry: (failureCount, error) => {
      if (isApiError(error) && error.status >= 500) {
        return failureCount < 3;
      }
      return false;
    },
    // Exponential backoff, capped at 5s. The base is intentionally small so a
    // hard-failing 5xx surfaces its error quickly (a slow operator gate is worse
    // than a fast one) while still spacing out transient retries.
    retryDelay: (attempt) => Math.min(RETRY_BASE_MS * 2 ** attempt, 5000),
  });

  useEffect(() => {
    if (
      organizationId &&
      isApiError(query.error) &&
      query.error.status === 403
    ) {
      clearOrganization();
    }
  }, [clearOrganization, organizationId, query.error]);

  if (query.isLoading) {
    return { kind: "loading" };
  }

  if (query.error) {
    if (
      isApiError(query.error) &&
      query.error.status === 404 &&
      query.error.code === ORG_NOT_FOUND_CODE
    ) {
      return { kind: "no-org" };
    }
    if (
      isApiError(query.error) &&
      query.error.status === 409 &&
      query.error.code === "ORG_SELECTION_REQUIRED"
    ) {
      const organizations = Array.isArray(query.error.details?.organizations)
        ? query.error.details.organizations.filter(isOrgWorkspaceResponse)
        : [];
      if (organizations.length > 0) {
        return { kind: "selection-required", organizations };
      }
    }
    const authorityUnknown =
      !isApiError(query.error) ||
      query.error.status === 429 ||
      query.error.status >= 500;
    return {
      kind: "error",
      message:
        (query.error instanceof Error && query.error.message) ||
        "Unable to load your organization.",
      ...(authorityUnknown && query.data
        ? {
            org: {
              ...query.data,
              can_manage_staff: false,
              finance_capability: "unavailable",
            },
            staffCapabilityPending: true,
          }
        : {}),
    };
  }

  if (!query.data) {
    return {
      kind: "error",
      message: "The server returned an invalid organization response.",
    };
  }

  // Keep the organization context usable during a refresh, but do not expose
  // cached privileged Team controls while its current capability is pending.
  if (query.isFetching) {
    return {
      ...toView({
        ...query.data,
        can_manage_staff: false,
        finance_capability: "unavailable",
      }),
      staffCapabilityPending: true,
    };
  }
  return toView(query.data);
}

// Short stale window (#998). The onboarding task composition is computed by the
// backend and CAN change under a long-lived session (a deploy that switches the
// launch-only service rules). A short staleTime lets a window-focus / reconnect
// refetch pull the current rules instead of serving a frozen task set until the
// operator signs out. Kept non-zero so the same render pass (page mount + child
// panels) shares one fetch rather than thrashing.
const ONBOARDING_STATE_STALE_TIME_MS = 30 * 1000;

/**
 * useOnboardingState — the backend-computed onboarding view for an org. Keyed by
 * org id. Refetches when the window regains focus or the network reconnects so a
 * long-lived session reflects the current backend onboarding rules WITHOUT a
 * full sign-out (#998). Disabled until an org id is known.
 */
export function useOnboardingState(orgId: string | null | undefined) {
  return useQuery<OnboardingState>({
    queryKey: ["org", orgId, "onboarding-state"],
    queryFn: () => getOnboardingState(orgId as string),
    enabled: !!orgId,
    staleTime: ONBOARDING_STATE_STALE_TIME_MS,
    // The #998 refresh: when the operator returns to the tab (the long-lived
    // session repro) or the network reconnects, re-pull the onboarding rules so
    // a post-deploy task-set change shows WITHOUT a sign-out. refetchOnMount is
    // left at its default — a fresh mount with no cached data fetches anyway,
    // and within the short staleTime a remount reuses the cache, so the page +
    // its child panels don't each issue a duplicate request.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export { ONBOARDING_STATUSES };
